import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, Coins, Download, Gamepad2, TrendingUp, UserSearch } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { Explain } from '@/components/Explain';
import { CopyId } from '@/components/primitives';
import { cn } from '@/lib/cn';
import type { Bootstrap } from '@/lib/bootstrap';
import { formatTime, parseClickhouseTime, type TimeZoneMode } from '@/lib/format';
import type { PlayerBreakdownRow, PlayerInspectionResult } from '@/types';
import { ApiError, fetchClients, fetchGames } from '@/features/bet-inspection/lib/api';
import { defaultRange, toClickhouseUtc } from '@/features/player-reports/lib/time';
import { ActivityCharts } from './components/ActivityCharts';
import { FilterBar, type InspectionFilters } from './components/FilterBar';
import { RiskPanel } from './components/RiskPanel';
import { SessionsDevices } from './components/SessionsDevices';
import { fetchPlayerInspection } from './lib/api';
import { downloadCsv, inspectionFilename, toCsv } from './lib/csv';

/**
 * Player Inspection — the whole picture of one player: per-unit breakdowns plus
 * a built-in fraud-signal panel.
 *
 * Everything is UTC and scoped to one project (a player_id is unique per
 * project). Breakdown rows keep their native currency; only cross-currency
 * roll-ups convert, and say so.
 */
export function PlayerInspection({
  boot,
  tz,
  base,
}: {
  boot: Bootstrap;
  tz: TimeZoneMode;
  base: string;
}) {
  const [filters, setFilters] = useState<InspectionFilters>(defaultFilters);
  const [applied, setApplied] = useState<InspectionFilters | null>(null);

  const clients = useQuery({
    queryKey: ['clients', boot.api],
    queryFn: ({ signal }) => fetchClients({ api: boot.api!, token: boot.token, signal }),
    enabled: Boolean(boot.api),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  // Game names for the per-game table — uuid → name.
  const games = useQuery({
    queryKey: ['games', boot.api, applied?.psid],
    queryFn: ({ signal }) =>
      fetchGames({ api: boot.api!, token: boot.token, playServiceId: applied!.psid, signal }),
    enabled: Boolean(boot.api && applied?.psid),
    staleTime: 5 * 60_000,
  });

  const run = useMutation<PlayerInspectionResult, Error, InspectionFilters>({
    mutationFn: (f) =>
      fetchPlayerInspection({
        api: boot.api!,
        token: boot.token,
        input: {
          play_service_id: f.psid,
          player_id: f.playerId.trim(),
          base,
          from: toClickhouseUtc(f.from),
          to: toClickhouseUtc(f.to),
        },
      }),
  });

  const result = run.data;
  const gameName = useMemo(() => {
    const map = new Map((games.data ?? []).map((g) => [g.game_uuid, g.name]));
    return (uuid: string) => map.get(uuid) || uuid;
  }, [games.data]);

  const clientName = useMemo(
    () =>
      (applied && (clients.data ?? []).find((c) => c.play_service_id === applied.psid)?.client_name) ||
      applied?.psid ||
      '',
    [clients.data, applied],
  );

  const search = () => {
    setApplied({ ...filters });
    run.mutate(filters);
  };

  return (
    <>
      <div className="sticky top-[41px] z-30 border-b border-hairline bg-bg/95 backdrop-blur">
        <FilterBar
          value={filters}
          onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))}
          onSearch={search}
          onReset={() => {
            setFilters(defaultFilters());
            setApplied(null);
            run.reset();
          }}
          loading={run.isPending}
          clients={clients.data ?? []}
          clientsLoading={clients.isLoading}
          clientsError={clients.isError}
          tz={tz}
        />
      </div>

      {run.isPending && <Scanning />}
      {run.isError && <Failure error={run.error} />}
      {!run.isPending && !run.isError && !result && <Idle />}

      {result && !run.isPending && (
        <main className="rise">
          <Header result={result} tz={tz} clientName={clientName} />
          <RiskPanel risk={result.risk} />

          {result.unconverted.length > 0 && (
            <div className="flex items-center gap-2 border-b border-hairline bg-warn-bg/40 px-4 py-1.5 text-[12px] text-text-dim">
              <span className="text-warn">▲</span>
              Excluded from the converted totals — no exchange rate:{' '}
              <span className="font-mono text-warn">{result.unconverted.join(', ')}</span>
            </div>
          )}

          <ActivityCharts perDay={result.perDay} perHour={result.perHour} tz={tz} unit={result.unit} />

          <Breakdown
            title="By game"
            icon={<Gamepad2 size={12} />}
            rows={result.perGame}
            label={(r) => gameName(r.key)}
          />
          <Breakdown
            title="By currency"
            icon={<Coins size={12} />}
            rows={result.perCurrency}
            label={(r) => r.key}
          />

          <SessionsDevices result={result} />

          <footer className="px-4 py-6 text-center text-[11px] leading-relaxed text-text-faint">
            ggr = bet − win − rollback · breakdown rows are in each unit's own currency · totals in{' '}
            {result.unit} · times UTC · the settled copy runs about an hour behind
          </footer>
        </main>
      )}
    </>
  );
}

function Header({
  result,
  tz,
  clientName,
}: {
  result: PlayerInspectionResult;
  tz: TimeZoneMode;
  clientName: string;
}) {
  const o = result.overview;
  const first = o.first_seen ? parseClickhouseTime(o.first_seen) : null;
  const last = o.last_seen ? parseClickhouseTime(o.last_seen) : null;
  const csv = toCsv(result);

  return (
    <section className="border-b border-hairline bg-surface">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-2.5">
        <UserSearch size={16} className="text-accent" />
        <Cell label={<Explain term="player_id">player</Explain>}>
          <CopyId value={o.player_id} className="text-[14px] font-semibold" />
        </Cell>
        <Cell label="project">
          <span className="font-mono text-[12px] text-text-dim">{clientName}</span>
        </Cell>
        <Cell label="currencies">
          <span className="font-mono text-[12px] text-text">{o.currencies.join(', ') || '—'}</span>
        </Cell>
        <Cell label="active days">
          <span className="tnum font-mono text-[13px] text-text">{o.active_days}</span>
        </Cell>
        <Cell label="first seen">
          <span className="tnum font-mono text-[11.5px] text-text-dim">
            {first ? formatTime(first, tz, true) : '—'}
          </span>
        </Cell>
        <Cell label="last seen">
          <span className="tnum font-mono text-[11.5px] text-text-dim">
            {last ? formatTime(last, tz, true) : '—'}
          </span>
        </Cell>

        <button
          type="button"
          onClick={() =>
            downloadCsv(inspectionFilename(clientName, o.player_id, o.first_seen ?? ''), csv)
          }
          className="ml-auto inline-flex items-center gap-1.5 border border-hairline px-2.5 py-1.5 font-condensed text-[11px] font-semibold uppercase tracking-wider text-text-dim hover:border-hairline-strong hover:text-text"
        >
          <Download size={12} /> Export CSV
        </button>
      </div>

      <div className="flex flex-wrap items-stretch divide-x divide-hairline border-t border-hairline">
        <Kpi label="Bets" value={String(o.bets)} sub={`${o.games} games · ${o.sessions} sessions`} />
        <Kpi label="Total Bet" value={fmt(o.bet)} sub="staked" currency={result.unit} />
        <Kpi label="Total Win" value={fmt(o.win)} sub="returned" currency={result.unit} />
        <Kpi
          label="GGR"
          icon={<TrendingUp size={12} />}
          value={fmt(o.ggr)}
          sub={`${(o.hold * 100).toFixed(1)}% hold`}
          currency={result.unit}
          tone={Number(o.ggr) < 0 ? 'warn' : undefined}
        />
        <div className="ml-auto flex items-center gap-1.5 px-4">
          {result.converted && (
            <span
              title={`Totals converted to ${result.unit} using the platform's exchange rates — approximate. Breakdown rows keep native currency.`}
              className="inline-flex items-center border border-info/40 bg-info-bg px-2 py-0.5 font-condensed text-[10px] font-semibold uppercase tracking-wider text-info"
            >
              fx → {result.unit}
            </span>
          )}
          <span className="inline-flex items-center border border-ok/40 bg-ok-bg px-2 py-0.5 font-condensed text-[10px] font-semibold uppercase tracking-wider text-ok">
            settled
          </span>
        </div>
      </div>
    </section>
  );
}

function Breakdown({
  title,
  icon,
  rows,
  label,
}: {
  title: string;
  icon: ReactNode;
  rows: PlayerBreakdownRow[];
  label: (r: PlayerBreakdownRow) => string;
}) {
  return (
    <section className="border-b border-hairline">
      <div className="flex items-center gap-2 px-4 py-2">
        <span className="flex items-center gap-1.5 font-condensed text-[12px] font-semibold uppercase tracking-wider text-text">
          {icon}
          {title}
        </span>
        <span className="tnum font-mono text-[11px] text-text-faint">{rows.length}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse whitespace-nowrap text-[12px]">
          <thead>
            <tr className="border-y border-hairline bg-surface-sunken">
              <Th align="left">{title.replace('By ', '')}</Th>
              <Th align="left">cur</Th>
              <Th>Bet</Th>
              <Th>Win</Th>
              <Th>GGR</Th>
              <Th>Hold</Th>
              <Th>Bets</Th>
              <Th>Rounds</Th>
              <Th>Sess</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.key}-${r.currency ?? ''}`} className="border-b border-hairline/50 last:border-0 hover:bg-surface-raised/50">
                <td className="max-w-[280px] truncate px-3 py-1.5 text-text">{label(r)}</td>
                <td className="px-3 py-1.5 font-mono text-[11px] text-text-faint">{r.currency ?? r.key}</td>
                <td className="px-3 py-1.5 text-right"><Amount value={r.bet} /></td>
                <td className="px-3 py-1.5 text-right"><Amount value={r.win} tone={Number(r.win) > 0 ? 'ok' : undefined} /></td>
                <td className="px-3 py-1.5 text-right"><Amount value={r.ggr} tone={Number(r.ggr) < 0 ? 'warn' : undefined} /></td>
                <td className="tnum px-3 py-1.5 text-right font-mono text-text-dim">{(r.hold * 100).toFixed(1)}%</td>
                <td className="tnum px-3 py-1.5 text-right font-mono text-text">{r.bets}</td>
                <td className="tnum px-3 py-1.5 text-right font-mono text-text-dim">{r.rounds}</td>
                <td className="tnum px-3 py-1.5 text-right font-mono text-text-dim">{r.sessions}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-5 text-center text-[12px] text-text-dim">
                  No activity.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Cell({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div>
      <div className="eyebrow mb-0.5">{label}</div>
      <div className="flex h-[20px] items-center">{children}</div>
    </div>
  );
}

function Kpi({
  label,
  value,
  sub,
  currency,
  icon,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  currency?: string;
  icon?: ReactNode;
  tone?: 'warn';
}) {
  return (
    <div className="min-w-[170px] px-4 py-2.5">
      <div className="eyebrow mb-1 flex items-center gap-1.5">
        {icon}
        {label}
      </div>
      <div className={cn('tnum font-mono text-[18px] font-semibold', tone === 'warn' ? 'text-warn' : 'text-text')}>
        {value}
        {currency && (
          <span className="ml-1 text-[11px] font-normal text-text-faint">{currency}</span>
        )}
      </div>
      {sub && <div className="mt-0.5 text-[11px] text-text-dim">{sub}</div>}
    </div>
  );
}

function Th({ children, align = 'right' }: { children?: ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={cn(
        'px-3 py-1.5 font-condensed text-[10px] font-semibold uppercase tracking-wider text-text-faint',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      {children}
    </th>
  );
}

function Amount({ value, tone }: { value: string; tone?: 'ok' | 'warn' }) {
  const n = Number(value);
  return (
    <span
      title={value}
      className={cn('tnum cursor-help font-mono', tone === 'warn' ? 'text-warn' : tone === 'ok' ? 'text-ok' : 'text-text')}
    >
      {Number.isFinite(n) ? fmt(value) : value}
    </span>
  );
}

function fmt(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function defaultFilters(): InspectionFilters {
  const { from, to } = defaultRange();
  return { psid: '', playerId: '', from, to };
}

function Scanning() {
  return (
    <div className="instrument-bg flex min-h-[40vh] flex-col items-center justify-center gap-4">
      <div className="lens-scan relative h-px w-[220px] overflow-hidden bg-hairline" />
      <p className="font-condensed text-[11px] uppercase tracking-[0.2em] text-text-dim">
        assembling this player across every unit
      </p>
    </div>
  );
}

function Idle() {
  return (
    <div className="instrument-bg flex min-h-[40vh] flex-col items-center justify-center gap-3 px-6 text-center">
      <UserSearch size={22} className="text-text-faint" />
      <p className="max-w-[500px] text-[13px] leading-relaxed text-text-dim">
        The whole picture of one player — per game and currency, activity over time, sessions and
        devices, plus built-in fraud signals that flag what is worth a second look.
      </p>
      <p className="max-w-[500px] text-[12px] leading-relaxed text-text-faint">
        Pick a project and enter a player id. The risk score is a heuristic aid for triage, never a
        verdict.
      </p>
    </div>
  );
}

function Failure({ error }: { error: Error }) {
  const status = error instanceof ApiError ? error.status : 0;
  return (
    <div className="flex min-h-[30vh] items-start justify-center px-6 pt-12">
      <div className="flex max-w-[520px] gap-3 border border-crit/40 bg-crit-bg p-4">
        <AlertCircle size={16} className="mt-[2px] shrink-0 text-crit" />
        <div>
          <div className="mb-1 font-condensed text-[11px] font-semibold uppercase tracking-wider text-crit">
            {status === 403 ? 'Not permitted' : 'Inspection failed'}
          </div>
          <p className="text-[12.5px] leading-relaxed text-text">{error.message}</p>
        </div>
      </div>
    </div>
  );
}
